import "fixed-data-table-2/dist/fixed-data-table.css";
import "./Table.css";
import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Table, Column, Cell, ColumnGroup } from "fixed-data-table-2";
import { isSeabird } from "util/seabird";

const TIME_DIFF_WARN_S = 5 * 60;
const TIME_DIFF_ERR_S = 8 * 60;
const DEPTH_DIFF_WARN_M = 5;
const DEPTH_DIFF_ERR_M = 10;
const SEABIRD_MODELS = ["SBE19plus", "SBE25", "SBE25plus"];

const styles = {
  textInputStyle: {
    width: "100%",
    background: "transparent",
    border: "none"
  },
  warnDark: {
    backgroundColor: "hsl(40, 100%, 89%)"
  },
  warnLight: {
    backgroundColor: "hsl(40, 100%, 93%)"
  },
  errorDark: {
    backgroundColor: "hsl(359, 87%, 89%)"
  },
  errorLight: {
    backgroundColor: "hsl(359, 87%, 93%)"
  },
  groupHeaderStyle: {
    fontWeight: "bold",
    marginTop: -2
  },
  headerStyle: {
    fontWeight: "bold",
    marginTop: 5
  },
  tableFont: {
    fontFamily: "Roboto",
    fontSize: "14px"
  },
  duplicateDark: {
    backgroundColor: "#ffa061",
    color: "white"
  },
  duplicateLight: {
    backgroundColor: "#ffe1cc"
  }
};

const getFormDuplicatesStyle = (ctdCastPk, duplicates, rowNum) => {
  if (duplicates.includes(ctdCastPk))
    return rowNum % 2 === 0 ? styles.duplicateLight : styles.duplicateDark;
  return {};
};

const getTimeCellStyle = (castTime, formTime, rowIndex) => {
  const timeDeltaMs = Math.abs(moment(castTime).diff(moment(formTime)));
  if (timeDeltaMs > TIME_DIFF_ERR_S * 1000) {
    return rowIndex % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (timeDeltaMs > TIME_DIFF_WARN_S * 1000) {
    return rowIndex % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const getDepthCellStyle = (castDepth, formDepth, rowNum) => {
  const depthDelta = Math.abs(formDepth - castDepth);
  if (depthDelta > DEPTH_DIFF_ERR_M) {
    return rowNum % 2 === 0 ? styles.errorLight : styles.errorDark;
  }
  if (depthDelta > DEPTH_DIFF_WARN_M) {
    return rowNum % 2 === 0 ? styles.warnLight : styles.warnDark;
  }
  return {};
};

const CastsInFileCard = props => {
  if (props.castsInFilesBeingAnnotatedList.length > 0) {
    return (
      <div style={styles.tableFont}>
        <Table
          rowsCount={props.castsInFilesBeingAnnotatedList.length}
          rowHeight={40}
          headerHeight={50}
          groupHeaderHeight={30}
          width={props.width - 5}
          height={
            (Math.min(15, props.castsInFilesBeingAnnotatedList.length) + 3) *
              35 +
            10
          }
        >
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Change</Cell>}
            fixed
          >
            <Column
              header="form"
              fixed
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const possibleForms = props.possibleForms[data.ctdCastPk];
                const style = getFormDuplicatesStyle(
                  data.ctdCastPk,
                  props.duplicateFormsInCastsBeingAnnotated,
                  rowIndex
                );

                // If there are no matches, make the button say so
                if (possibleForms.length === 1 && !possibleForms[0].date) {
                  return (
                    <Cell {...colProps}>
                      <input type="button" value="no match" />
                    </Cell>
                  );
                }
                return (
                  <Cell {...colProps} style={style}>
                    <input
                      type="button"
                      disabled={data?.noCast || false}
                      value={`${possibleForms.length} matches`}
                      onClick={() => props.onCastChangeFormClick(data)}
                      style={style}
                    />
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Cast</Cell>}
            fixed
          >
            <Column
              header="data"
              fixed
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                return (
                  <Cell {...colProps}>
                    <input
                      type="button"
                      value="view chart"
                      onClick={() => props.onCreateChart(data)}
                      disabled={SEABIRD_MODELS.some(v =>
                        data.filename.includes(v)
                      )}
                    />
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
            <Column
              header="map"
              fixed
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const matchedForm = props.matchedForms[data.ctdCastPk];
                const locationFields = [
                  matchedForm.startLocation,
                  matchedForm.bottomLocation,
                  matchedForm.endLocation
                ];
                return (
                  <Cell {...colProps}>
                    <input
                      type="button"
                      value="view map"
                      onClick={() => props.onCreateMap(data)}
                      disabled={
                        locationFields.every(elem => elem === null) ||
                        matchedForm.siteId === null ||
                        !(matchedForm.siteId in props.siteData)
                      }
                    />
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={
              <Cell style={styles.groupHeaderStyle}>Instrument time</Cell>
            }
          >
            <Column
              header="date"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                return (
                  <Cell {...colProps}>{moment(data.startDt).format("ll")}</Cell>
                );
              }}
              width={110}
              flexGrow={1}
            />
            <Column
              header="start"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const dt = moment(data.startDt);
                const style = getTimeCellStyle(
                  data.startDt,
                  form.dropStartTime,
                  rowIndex
                );
                return (
                  <Cell {...colProps} style={style} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
            <Column
              header="bottom"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const dateTime = moment(data.bottomDt);
                let dt;
                let style;
                if (!SEABIRD_MODELS.includes(data.deviceModel)) {
                  dt = dateTime.format("LT");
                  style = getTimeCellStyle(
                    data.bottomDt,
                    form.dropBottomTime,
                    rowIndex
                  );
                } else if (data.bottomDt && data.endDt) {
                  dt = dateTime.format("LT");
                  style = getTimeCellStyle(
                    data.bottomDt,
                    form.dropBottomTime,
                    rowIndex
                  );
                } else {
                  dt = "-";
                  style = {};
                }
                return (
                  <Cell {...colProps} style={style} title={dt}>
                    {dt}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
            <Column
              header="end"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                let dt;
                let style;
                if (SEABIRD_MODELS.includes(data.deviceModel)) {
                  if (data.endDt) {
                    dt = moment(data.endDt).format("LT");
                  } else {
                    dt = moment(data.bottomDt).format("LT");
                  }
                  style = getTimeCellStyle(
                    data.bottomDt,
                    form.dropEndTime,
                    rowIndex
                  );
                } else if (data.endDt) {
                  dt = moment(data.endDt).format("LT");
                  style = getTimeCellStyle(
                    data.endDt,
                    form.dropEndTime,
                    rowIndex
                  );
                } else {
                  dt = "-";
                  style = {};
                }
                return (
                  <Cell {...colProps} style={style} title={dt}>
                    {dt}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Form time</Cell>}
          >
            <Column
              header="start"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                if (!form.dropStartTime) return <Cell {...colProps}>-</Cell>;
                const dt = moment(form.dropStartTime);
                const style = getTimeCellStyle(
                  data.startDt,
                  form.dropStartTime,
                  rowIndex
                );
                return (
                  <Cell {...colProps} style={style} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
            <Column
              header="bottom"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                if (!form.dropBottomTime) return <Cell {...colProps}>-</Cell>;
                const dt = moment(form.dropBottomTime);
                const style = !SEABIRD_MODELS.includes(data.deviceModel)
                  ? getTimeCellStyle(
                      data.bottomDt,
                      form.dropBottomTime,
                      rowIndex
                    )
                  : {};
                return (
                  <Cell {...colProps} style={style} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
            <Column
              header="end"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                if (!form.dropEndTime) return <Cell {...colProps}>-</Cell>;
                const dt = moment(form.dropEndTime);
                const style = SEABIRD_MODELS.includes(data.deviceModel)
                  ? getTimeCellStyle(data.bottomDt, form.dropEndTime, rowIndex)
                  : {};
                return (
                  <Cell {...colProps} style={style} title={dt.format()}>
                    {dt.format("LT")}
                  </Cell>
                );
              }}
              width={77}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={
              <Cell style={styles.groupHeaderStyle}>Instrument depth</Cell>
            }
          >
            <Column
              header="surface"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const startDepth = data.startDepth
                  ? `${Math.round(data.startDepth * 10) / 10} m`
                  : "-";
                return (
                  <Cell {...colProps} title={startDepth}>
                    {startDepth}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
            <Column
              header="bottom"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const bottomDepth = data.bottomDepth
                  ? `${Math.round(data.bottomDepth * 10) / 10} m`
                  : "-";
                const style = isSeabird(data.filename)
                  ? {}
                  : getDepthCellStyle(
                      data.bottomDepth,
                      form.targetDepthM,
                      rowIndex
                    );
                return (
                  <Cell {...colProps} style={style} title={bottomDepth}>
                    {bottomDepth}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>Form depth</Cell>}
          >
            <Column
              header="target"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const depth = form.targetDepthM
                  ? `${form.targetDepthM} m`
                  : "-";
                const style = isSeabird(data.filename)
                  ? {}
                  : getDepthCellStyle(
                      data.bottomDepth,
                      form.targetDepthM,
                      rowIndex
                    );
                return (
                  <Cell {...colProps} style={style}>
                    {depth}
                  </Cell>
                );
              }}
              width={100}
              flexGrow={1}
            />
          </ColumnGroup>
          <ColumnGroup
            header={
              <Cell style={styles.groupHeaderStyle}>Form attributes</Cell>
            }
          >
            <Column
              header="Survey"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const survey = form.survey ? form.survey : "-";
                return (
                  <Cell {...colProps} title={survey}>
                    {survey}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
            <Column
              header="Site"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const siteId = form.siteId ? form.siteId : "-";
                return (
                  <Cell {...colProps} title={siteId}>
                    {siteId}
                  </Cell>
                );
              }}
              width={90}
              flexGrow={1}
            />
            <Column
              header="Site dist."
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const dist = form.distanceFromSampleSite
                  ? `${form.distanceFromSampleSite} m`
                  : "-";
                return <Cell {...colProps}>{dist}</Cell>;
              }}
              width={100}
              flexGrow={1}
            />
            <Column
              header="Drop"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const drop = form.drop ? form.drop : "-";
                return <Cell {...colProps}>{drop}</Cell>;
              }}
              width={50}
              flexGrow={1}
            />
            <Column
              header="Cast Type"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const castType = form.castType ? form.castType : "-";
                return <Cell {...colProps}>{castType.slice(0, 3)}</Cell>;
              }}
              width={50}
              flexGrow={1}
            />
            <Column
              header="Miscast"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                const { miscast } = form;
                return (
                  <Cell {...colProps}>
                    <input
                      type="checkbox"
                      checked={miscast}
                      onChange={() =>
                        props.onCastMiscastCheck(data.ctdCastPk, !miscast)
                      }
                      disabled={data?.noCast || false} // meaningless to set if marked toggle-miscast
                    />
                  </Cell>
                );
              }}
              width={70}
              flexGrow={1}
            />
            {props.castsInFilesBeingAnnotatedList.every(
              cast => cast?.noCast
            ) ? (
              <Column
                header="Toggle Miscast"
                cell={<Cell></Cell>}
                width={70}
                flexGrow={1}
              />
            ) : (
              <Column
                header="Toggle Miscast"
                cell={({ rowIndex, ...colProps }) => {
                  const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                  const { noCast, ctdFilePk } = data;
                  return (
                    <Cell {...colProps}>
                      <input
                        type="checkbox"
                        checked={noCast}
                        onChange={() =>
                          props.onNoCastCheck(
                            ctdFilePk,
                            data.ctdCastPk,
                            !noCast
                          )
                        }
                      />
                    </Cell>
                  );
                }}
                width={70}
                flexGrow={1}
              />
            )}

            <Column
              header="Crew"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                return (
                  <Cell {...colProps}>
                    <input
                      type="text"
                      style={styles.textInputStyle}
                      value={form.fieldStaff || ""}
                      onChange={e =>
                        props.onCastCrewChange(data.ctdCastPk, e.target.value)
                      }
                    />
                  </Cell>
                );
              }}
              width={100}
              flexGrow={2}
            />
            <Column
              header="Comments"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                const form = props.matchedForms[data.ctdCastPk];
                return (
                  <Cell {...colProps}>
                    <input
                      type="text"
                      style={styles.textInputStyle}
                      value={form.qualityLog || ""}
                      onChange={e =>
                        props.onCastCommentsChange(
                          data.ctdCastPk,
                          e.target.value
                        )
                      }
                    />
                  </Cell>
                );
              }}
              width={160}
              flexGrow={2}
            />
          </ColumnGroup>
          <ColumnGroup
            header={<Cell style={styles.groupHeaderStyle}>File meta</Cell>}
          >
            <Column
              header="Filename"
              cell={({ rowIndex, ...colProps }) => {
                const data = props.castsInFilesBeingAnnotatedList[rowIndex];
                return <Cell {...colProps}>{data.filename}</Cell>;
              }}
              width={300}
              flexGrow={1}
            />
          </ColumnGroup>
        </Table>
      </div>
    );
  }
  return null;
};

const formShape = PropTypes.shape({
  dropStartTime: PropTypes.string,
  dropBottomTime: PropTypes.string,
  survey: PropTypes.string,
  siteId: PropTypes.string,
  distanceFromSampleSite: PropTypes.number,
  targetDepthM: PropTypes.number,
  drop: PropTypes.number,
  miscast: PropTypes.bool,
  fieldStaff: PropTypes.string,
  qualityLog: PropTypes.string
});

const castShape = PropTypes.shape({
  ctdFilePk: PropTypes.number.isRequired,
  ctdCastPk: PropTypes.number.isRequired,
  startDt: PropTypes.string.isRequired,
  bottomDt: PropTypes.string.isRequired,
  endDt: PropTypes.string,
  filename: PropTypes.string.isRequired,
  noCast: PropTypes.bool.isRequired,
  deviceModel: PropTypes.string.isRequired,
  manualOverrideDt: PropTypes.bool,
  startDepth: PropTypes.number, // could be null for seabird data until file is initially processed
  bottomDepth: PropTypes.number, // could be null for seabird data until file is initially processed
  endDepth: PropTypes.number // could be null for seabird data until file is initially processed
});

CastsInFileCard.propTypes = {
  width: PropTypes.number,
  castsInFilesBeingAnnotatedList: PropTypes.arrayOf(castShape),
  possibleForms: PropTypes.objectOf(PropTypes.arrayOf(formShape)),
  matchedForms: PropTypes.objectOf(formShape),
  onCastChangeFormClick: PropTypes.func.isRequired,
  onCastMiscastCheck: PropTypes.func.isRequired,
  onNoCastCheck: PropTypes.func.isRequired,
  onCastCrewChange: PropTypes.func.isRequired,
  onCastCommentsChange: PropTypes.func.isRequired,
  onCreateChart: PropTypes.func.isRequired,
  onCreateMap: PropTypes.func.isRequired,
  duplicateFormsInCastsBeingAnnotated: PropTypes.arrayOf(PropTypes.number)
    .isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  siteData: PropTypes.object.isRequired
};

CastsInFileCard.defaultProps = {
  width: 900,
  castsInFilesBeingAnnotatedList: [],
  possibleForms: {},
  matchedForms: {}
};

export default CastsInFileCard;
